import { useState } from 'react';

import { get } from 'lodash';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import storage from '../services/storageService';

import useCurrentUser from './useCurrentUser';

// Hook
function useLocalStorage(keyProp: string, initialValue: any, shouldPersist?: boolean) {
  const user = useCurrentUser();
  const key = shouldPersist ? `${keyProp}:persist:${get(user, '_id', '')}` : keyProp;
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = storage.get(key);
      // use initialValue only if there is no valid value. false and 0 are a valid values
      return item !== undefined && item !== null ? item : initialValue;
    } catch (error) {
      // If error also return initialValue
      sendLog('Error on @lib/hooks/useLocalStorage', error, LoggerStatusEnum.error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: any) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      storage.set(key, valueToStore);
    } catch (error) {
      // A more advanced implementation would handle the error case
      sendLog('Error on @lib/hooks/useLocalStorage.ts setValue', error, LoggerStatusEnum.error);
    }
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
